import Image from 'next/image';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import Amanda from '@/assets/health-care-heroes/Amanda-M.jpg';
import Andy from '@/assets/health-care-heroes/Andy.jpg';
import Angela from '@/assets/health-care-heroes/Angela-P.jpg';
import Britany from '@/assets/health-care-heroes/Brittany-C.jpg';
import Chris from '@/assets/health-care-heroes/Chris-M.jpg';
import Courtney from '@/assets/health-care-heroes/Courtney-B.jpg';
import Danielle from '@/assets/health-care-heroes/Danielle-M.jpg';
import Gary from '@/assets/health-care-heroes/Gary-K.jpg';
import HeatherC from '@/assets/health-care-heroes/Heather-C.jpg';
import LianaG from '@/assets/health-care-heroes/Liana-G.jpg';
import LoganL from '@/assets/health-care-heroes/Logan-L.jpg';
import LoriH from '@/assets/health-care-heroes/Lori-H.jpg';
import MattS from '@/assets/health-care-heroes/Matt-S.jpg';
import PamelaC from '@/assets/health-care-heroes/Pamela-C.jpg';
import RochelleW from '@/assets/health-care-heroes/Rochelle-W.jpg';
import RodneyR from '@/assets/health-care-heroes/Rodney-R.jpg';
import TarynM from '@/assets/health-care-heroes/Taryn-M.jpg';
import TawnyO from '@/assets/health-care-heroes/Tawny-O.jpg';
import TommyF from '@/assets/health-care-heroes/Tommy-F.jpg';
import ZacharyB from '@/assets/health-care-heroes/Zachary-B.jpg';
import Button, { ButtonVariants } from '@/components/switchback/Button/Button';

import css from './HealthCareHeroes.module.css';

const HealthCareHeroes = () => {
  const [openSections, setOpenSections] = useState<number[]>([]);
  const [selectedHeroes, setSelectedHeros] = useState(heros.slice(0, 10));
  const intl = useIntl();

  const showMoreTextButton = intl.formatMessage({
    defaultMessage: 'Show more',
    id: 'aWpBzj',
  });
  const showLessTextButton = intl.formatMessage({
    defaultMessage: 'Show less',
    id: 'qyJtWy',
  });

  const readMoreTextButton = intl.formatMessage({
    defaultMessage: 'Read more',
    id: 'S++WdB',
  });
  const readLessTextButton = intl.formatMessage({
    defaultMessage: 'Read less',
    id: 'Ed2ywJ',
  });

  const readMoreText = (isFullText: boolean) => {
    return isFullText ? readLessTextButton : readMoreTextButton;
  };

  const toggleReadMoreIndex = (index: number) => {
    let newState: number[] = [];
    if (openSections.includes(index)) {
      newState = newState.filter(value => value === index);
    } else {
      newState.push(index);
    }
    setOpenSections(newState);
  };

  const toggleHerosCount = () => {
    let herosArray = heros;

    if (selectedHeroes.length === heros.length) {
      herosArray = herosArray.slice(0, 10);
    }

    setSelectedHeros(herosArray);
  };

  return (
    <div className={`mx-auto px-2 mt-6 mb-20 ${css.width}`}>
      <div className="mx-auto grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 lg:max-w-none">
        {selectedHeroes.map((hero, index) => {
          const isFullText = openSections.includes(index);
          return (
            <div key={hero.name} className="flex flex-col overflow-hidden rounded-lg shadow-lg">
              <div className="relative w-full h-64 shrink-0">
                <Image layout="fill" objectFit="cover" src={hero.photo} alt={hero.name} />
              </div>
              <div className="flex flex-col justify-between flex-1 p-6 bg-white">
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-600 leading-5">{hero.work}</p>
                  <h3 className="mt-2 text-xl font-medium text-gray-900 leading-7">{hero.name}</h3>
                  <div
                    className={`mt-3 text-base leading-6 text-gray-500 ${
                      isFullText ? '' : css.truncate
                    }`}
                    dangerouslySetInnerHTML={{ __html: hero.quote }}
                  />
                  <button
                    className="mt-2 text-sm font-medium text-gray-800 focus:outline-none"
                    onClick={() => toggleReadMoreIndex(index)}>
                    {readMoreText(isFullText)}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="max-w-xs mx-auto mt-12">
        <Button onClick={toggleHerosCount} variant={ButtonVariants.whiteContained}>
          {selectedHeroes.length === heros.length ? showLessTextButton : showMoreTextButton}
        </Button>
      </div>
    </div>
  );
};

export default HealthCareHeroes;

const heros = [
  {
    name: 'Matt S.',
    photo: MattS,
    work: 'COVID ICU Nurse from Massachusetts',
    quote:
      'Matt quickly volunteered to work on the dedicated COVID ICU floor in Boston, MA. He learned new skills, has taken care of the sickest COVID patients, and picked up many extra 12 hour shifts to fill the need.',
  },
  {
    name: 'Brittany C.',
    photo: Britany,
    work: 'Registered Nurse from Florida',
    quote:
      'She is currently working in the trenches of the COVID-19 front lines. The stories she tells me of the things she is seeing and experiencing as she serves these patients are shocking, but every day she goes back to work with a smile on her face, ready to serve her patients and her team. Despite fears expressed by her family and friends and the sadness of missing out on our summer plans, she ran towards the fight. ',
  },
  {
    name: 'Lori H.',
    work: 'Nurse from Colorado',
    photo: LoriH,
    quote:
      'Lori is responsible for calling individuals and notifying them if the COVID 19 test was positive. This free vacation allows her to step away from the stress of her work situation. Nature is how she recharges. After recharging this will allow her to come back to her job to handle the stress with a clear mind.',
  },
  {
    name: 'Tommy F.',
    work: 'Nurse from North Carolina',
    photo: TommyF,
    quote:
      'When the pandemic hit my husband signed up to be the charge nurse of the COVID ICU at Rex Hospital in Raleigh, NC. Tommy has stood up the ICU, and has been working tirelessly saving lives. He’s worked overtime. This could be a chance for our family to reconnect and make up for the time spent apart. For my husband to see how much his children have grown over the last few months. And it will give him a much needed break from working so hard, being on his feet all day caring for others.',
  },
  {
    name: 'Pamela C.',
    work: 'ICU Pharmacist from California',
    photo: PamelaC,
    quote:
      'She is taking care of COVID patiences day in and day out with great compassion and care despite the challenges through PPE shortages and quarantining herself from her family including her daughters. Pamela is also a US Air Force Veteran who served during 9/11 and has just re-enlisted with the US Army Reserves to again serve the great people of this nation.  She has always been selfless giving back to her patients, giving back to her community, and giving back to her country.',
  },
  {
    name: 'Logan	L.',
    work: 'Nurse from Texas',
    photo: LoganL,
    quote:
      "My husband Logan is such an amazing nurse and I don't think he always gets the praise that he deserves. He will take the assignment/patient that’s in the most critical condition and steps up when others are scared. He’s a great caregiver and people trust their lives with him.",
  },
  {
    name: 'Andy M.',
    work: 'Registered Nurse from Colorado',
    photo: Andy,
    quote:
      "Because he has extensive experience in the ICU, he was asked to change departments and start caring for COVID+ patients. He's a brave, strong, smart man and he definitely had some tough, devastating shifts, but we all survived. He self-quarantined to the basement, and wore a mask all the time while in the house. We have 2 toddlers, so it was tough limiting his interaction with them. I'm very proud of my husband for stepping up to the plate when he was needed most.",
  },
  {
    name: 'Amanda M.',
    work: 'Registered Nurse from Kentucky',
    photo: Amanda,
    quote:
      'Amanda has been working the frontlines in the ICU since mid March. She’s compassionately cared for patients like never before as patients have been in full isolation rooms, on ventilators and without any support from their family members. I know she’s grown so much during this pandemic and I am so proud of how she’s carried herself through it all. Her mental health has been affected, as one would expect, but she’s the most optimistic girl I know. She lives everyday with a positive outlook and that’s exactly the way she’s cared for these incredibly sick patients.',
  },
  {
    name: 'Liana G.',
    work: 'Nurse from California',
    photo: LianaG,
    quote:
      'For the past 3 months she has been caring for critically-ill covid patients on ventilators. Her work required her to do mandatory overtime and although my wife is strong, I see the stress that she brings home everyday. To protect our 3 year old daughter and parents, she has been self isolating at another location for 3 months now. She facetimes our daughter every night and visits her on her days off with mask, gloves, and social distancing.',
  },
  {
    name: 'Taryn M.',
    work: 'Registered Nurse from Massachusetts',
    photo: TarynM,
    quote:
      'Taryn has tirelessly worked through this pandemic being deployed throughout the hospital as a frontline worker. Taryn has worked in all capacities within the hospital. Taryn and her husband Josh have two school age children 6 & 12. Together while both working 40+ hours and covering mandatory calls have managed to also be teachers to the children. Trying to be everything to everybody is exhausting but both Taryn and Josh have kept moving forward.',
  },
  {
    name: 'Zachary B.',
    work: 'Nurse from West Virginia',
    photo: ZacharyB,
    quote:
      'After breaking his hip in February, he only took 3 weeks off to recover and then went right back to work at the nursing home because they needed all the help they could get after COVID-19 hit our area. He always puts others before himself. He is now muscling through 12+ hour shifts on a recently broken hip, just to be there for his residents.',
  },
  {
    name: 'Chris M.',
    work: 'Doctor from New Jersey',
    photo: Chris,
    quote:
      'Whether it was managing ventilator settings, inserting central lines, placing orders, or directly performing chest compressions during CPR, Chris has gone above and behind for his many patients with COVID 19. Despite being forced to wear the same N95 mask for weeks at a time due to PPE scarcity, he continued to fight on putting his patient’s safety before his own.',
  },
  {
    name: 'Rochelle W.',
    work: 'Registered Nurse from California',
    photo: RochelleW,
    quote:
      'Since the virus hit, she has been working at 2 hospitals and most time clocking over 70+ hrs a week. She is honestly my inspiration because throughout everything that has been going on she has maintained a positive and loving outlook. This vacation will mean the world to her. She has buckled down, studied, and accomplished her dream of becoming an RN and hit the ground running. She has been working herself to the bone for so many other people and hasn’t gotten to take any time for herself.',
  },
  {
    name: 'Heather C.',
    work: 'X-ray Technologist from Connecticut',
    photo: HeatherC,
    quote:
      "She is always putting others before herself, everyone she knows is a priority to her.  Her family and her friends mean everything to her. A trip would help Heather decompress and get her mind and body to a 'normal' it's used to. She would love to share this with her boys as well as me, her husband.",
  },
  {
    name: 'Angela P.',
    work: 'Registered Psychiatric Nurse from British Columbia',
    photo: Angela,
    quote:
      'My girlfriend was working at the psychiatric unit which was an assigned COVID hospital in the region. Since the pandemic started, their mental health unit was assigned to hold the pending and covid positive patients. She has since had to go home and isolate by herself in the basement when she’s at home. This trip will really help Angela decompress and alleviate some of the stress caused by the pandemic.',
  },
  {
    name: 'Rodney R.',
    work: 'EMT from New Jersey',
    photo: RodneyR,
    quote:
      "Rodney is a kind man who has dedicated his life to the medical field regularly working 5 twelve hour shifts a week (more during the pandemic). Rodney has dreamed of taking a Roadtrip in an RV. He always speaks of 'someday' renting or buying one to start our family adventure. I think this trip would give him much needed time to reconnect with family and fulfill a lifelong dream.",
  },
  {
    name: 'Tawny	O.',
    work: 'Pharmacist from Minnesota',
    photo: TawnyO,
    quote:
      'She works tirelessly in the ED and throughout the hospital, helping the team with coding patients, traumas, or anything that may roll through the doors. As the ED is seeing more and more COVID or potential COVID patients, I know she is stressed about the possibility of bringing it home to our baby (or me), but she will always show up and give her best. Then she comes home after very long shifts and cares for our baby girl, who just lights up when she sees her mom. Tawny is a hero to me, our baby and fur babies and to her patients and it would be wonderful to take her on a road trip when all this is over.',
  },
  {
    name: 'Danielle M.',
    work: 'Registered Nurse from Texas',
    photo: Danielle,
    quote:
      'Danielle works in the ICU and is currently 8 months pregnant. She has been working full time throughout her pregnancy and dealing with Covid-19 patients. She has been exceptionally worried about contracting the virus and putting the health of their child at risk. My wife has a limited about of maternity leave and is working up until the birth so she can spend more time with the little one before returning back to work.',
  },
  {
    name: 'Gary K.',
    work: 'Emergency Doctor from Michigan',
    photo: Gary,
    quote:
      'Gary is currently an Emergency resident in Detroit, MI and has been serving on the frontlines of the pandemic from the beginning. Gary put his own safety at risk to help those that were sick and also scared for their lives, and he ended up contracting the virus and had to spend 2 weeks at home recovering from severe fatigue, cough, and fevers. Thankfully he did get better and is now back at work. Gary deserves some “me time,” and a celebratory congratulations on graduating, as well as a huge thank you for his work, empathy, and sacrifice during the pandemic.',
  },
  {
    name: 'Courtney B.',
    work: 'Medic from Colorado',
    photo: Courtney,
    quote:
      "Courtney is a medic and volunteer firefighter who lives in Colorado and deployed to the frontlines in NYC to help fight the COVID battle. She deployed for 40 days and while on the frontlines contracted COVID and had a 'silent' heart attack related to COVID. Courtney is struggling mentally from the mass amount of trauma on the NY frontlines and could use a safe space to get away in.",
  },
];
